import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client";
import { useAppState } from "../../state/appState";
import { Empty, StatusChip } from "../../ui/components";
import { PAYMENT_STATUS_LABELS, dateTime, money } from "../../ui/format";

interface StuckRefund {
  id: string;
  orderNumber: number | null;
  amount: number;
  attempts: number;
  lastError: string | null;
  createdAt: string;
}

export function PaymentsPage() {
  const { snapshot } = useAppState();

  if (!snapshot) return <Empty>Загрузка данных…</Empty>;

  const paid = snapshot.orders.filter(order => order.paymentStatus !== "created");
  const refunded = snapshot.orders.filter(order => order.refundAmount > 0);
  const totalPaid = snapshot.orders
    .filter(order => order.paymentStatus === "paid")
    .reduce((sum, order) => sum + order.total, 0);
  const totalRefunded = refunded.reduce((sum, order) => sum + order.refundAmount, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <p className="eyebrow">Деньги</p>
          <h1>Платежи и возвраты</h1>
        </div>
        <span className="chip">Альфа-Банк</span>
      </div>

      <div className="metrics">
        <div className="metric">
          <b className="num">{money(totalPaid)}</b>
          <span>Оплачено</span>
        </div>
        <div className="metric">
          <b className="num">{money(totalRefunded)}</b>
          <span>Возвращено</span>
        </div>
        <div className="metric">
          <b className="num">{refunded.length}</b>
          <span>Заказов с возвратом</span>
        </div>
      </div>

      <StuckRefunds />

      <section className="panel">
        <header>
          <div>
            <h2>Платёжные операции</h2>
            <p>Деньги принимает Альфа-Банк, чек пробивает касса на точке.</p>
          </div>
        </header>

        {paid.length === 0 ? (
          <Empty>Платежей пока нет.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Заказ</th>
                  <th>Оплачен</th>
                  <th>Статус оплаты</th>
                  <th>Статус заказа</th>
                  <th>Сумма</th>
                  <th>Возврат</th>
                </tr>
              </thead>
              <tbody>
                {paid.map(order => (
                  <tr key={order.id}>
                    <td className="num">№{order.number}</td>
                    <td className="num">{dateTime(order.paidAt)}</td>
                    <td className="tiny">{PAYMENT_STATUS_LABELS[order.paymentStatus]}</td>
                    <td>
                      <StatusChip status={order.status} />
                    </td>
                    <td className="num">{money(order.total)}</td>
                    <td className="num">
                      {order.refundAmount ? (
                        <>
                          {money(order.refundAmount)}
                          <br />
                          <small className="muted">
                            {order.refundStatus ? PAYMENT_STATUS_LABELS[order.refundStatus] : ""}
                          </small>
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Возвраты, не дошедшие до банка.
 *
 * Возврат в нашей базе и возврат на карте — разные события: между ними лежит
 * сеть, и банк её иногда не переживает. Пока такой возврат не виден на экране,
 * первым о нём узнаёт гость, у которого деньги не вернулись.
 *
 * Карточки нет вовсе, когда возвращать нечего: пустая рамка «всё хорошо»
 * приучает глаз проскакивать это место.
 */
function StuckRefunds() {
  const [stuck, setStuck] = useState<StuckRefund[]>([]);

  const load = useCallback(async () => {
    try {
      const data = await api.get<{ stuck: StuckRefund[] }>("/api/admin/payments/refunds");
      setStuck(data.stuck);
    } catch {
      // Молчим: экран про деньги в целом, а не про эту карточку, и рушить
      // его из-за недоступного списка неправильно.
      setStuck([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (stuck.length === 0) return null;

  return (
    <section className="panel">
      <header>
        <div>
          <h2>Деньги не вернулись гостю</h2>
          <p>
            Возврат проведён у нас и вычтен из выручки, а банк его не принял. Верните деньги
            из личного кабинета банка — сама система больше пытаться не будет.
          </p>
        </div>
      </header>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Заказ</th>
              <th>Когда</th>
              <th className="num">Сумма</th>
              <th className="num">Попыток</th>
              <th>Что ответил банк</th>
            </tr>
          </thead>
          <tbody>
            {stuck.map(refund => (
              <tr key={refund.id}>
                <td className="num">
                  {refund.orderNumber === null ? "—" : `№${refund.orderNumber}`}
                </td>
                <td className="muted">{dateTime(refund.createdAt)}</td>
                <td className="num">{money(refund.amount)}</td>
                <td className="num">{refund.attempts}</td>
                <td className="tiny muted">{refund.lastError ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
